import { useMemo } from "react";
import type { Client, Entry } from "@ascente/shared-types";
import { calcClientTotals, type TotalsSummary } from "../utils/billing.js";
import { parseDate } from "../utils/dateHelpers.js";

export function useDashboardTotals(
  clients: Client[],
  entries: Entry[],
  rangeStart: string,
  rangeEnd: string,
): TotalsSummary {
  return useMemo(() => {
    const activeClients = clients.filter((c) => c.status === "active");
    const start = rangeStart ? parseDate(rangeStart) : new Date(0);
    const end = rangeEnd ? parseDate(rangeEnd) : new Date(9999, 11, 31);

    const byClient: TotalsSummary["byClient"] = {};
    for (const client of activeClients) {
      byClient[client.id] = calcClientTotals(client, entries, start, end);
    }

    const totals = Object.values(byClient);
    const grandSubtotal = totals.reduce((s, t) => s + t.subtotal, 0);
    const grandGST = totals.reduce((s, t) => s + t.gst, 0);
    const grandTotal = totals.reduce((s, t) => s + t.total, 0);
    const totalHours = totals.reduce((s, t) => s + t.hours, 0);
    const totalHourlyCharges = totals.reduce((s, t) => s + t.hourlyTotal, 0);
    const totalTaskCharges = totals.reduce((s, t) => s + t.taskCharges, 0);
    const totalFixedCharges = totals.reduce((s, t) => s + t.fixedTotal, 0);

    return {
      byClient,
      grandSubtotal,
      grandGST,
      grandTotal,
      totalHours,
      totalHourlyCharges,
      totalTaskCharges,
      totalFixedCharges,
    };
  }, [clients, entries, rangeStart, rangeEnd]);
}
